import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }
function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }
import { ProductType } from '@chegg/pricing-sdk';
import { ProductFamilyType, PRSProductFamilyTypeInput } from './product-recommendation-dgs.types';
import { VendorType } from '../../gql/globalTypes';
import { formatCurrency } from '../currencyFormatter';
import { isWindowDefined, getAuthState } from '@chegg/utils';
var PRODUCT_FAMILY_TYPE_MAP = Object.freeze(new Map([[ProductFamilyType.CHEGG_STUDY, ProductType.CHEGG_STUDY], [ProductFamilyType.CHEGG_STUDY_PACK, ProductType.CHEGG_STUDY_PACK], [ProductFamilyType.CHEGG_WRITING, ProductType.WRITING_TOOLS], [ProductFamilyType.CHEGG_MATH, ProductType.CHEGG_MATH]]));
var PRODUCT_CODE_MAP = Object.freeze(new Map([[ProductType.CHEGG_STUDY, PRSProductFamilyTypeInput.CHEGG_STUDY], [ProductType.CHEGG_STUDY_PACK, PRSProductFamilyTypeInput.CHEGG_STUDY_PACK], [ProductType.WRITING_TOOLS, PRSProductFamilyTypeInput.CHEGG_WRITING], [ProductType.CHEGG_MATH, PRSProductFamilyTypeInput.CHEGG_MATH]]));

/**
 * Maps legacy product types to one-graph product families
 * @param productType ProductType; pricing-sdk
 * @returns ProductFamilyType; one-graph
 */
var toDGSProductType = function toDGSProductType(productType) {
  var productFamilyType = PRODUCT_CODE_MAP.get(productType);
  if (!productFamilyType) throw new Error('could not map product code');
  return productFamilyType;
};

/**
 * Maps one-graph families to legacy product types
 * @param productFamilyType ProductFamilyType; one-grap
 * @returns ProductType; pricing-sdk
 */
var toPricingSdkProductType = function toPricingSdkProductType(productFamilyType) {
  var productType = PRODUCT_FAMILY_TYPE_MAP.get(productFamilyType);
  if (!productType) throw new Error('could not map product type');
  return productType;
};

/**
 * Temporarly lookup of currency information for US users; will be
 * re-implemented by a currency utility in the future
 * @param currencyCode string; ISO 4217 three-letter code
 * @returns CurrencyDetails
 */
var getCurrencyDetails = function getCurrencyDetails(currencyCode) {
  // TODO integrate currency format util
  return currencyCode === 'USD' ? {
    currencyName: 'dollar',
    currencyCode: 'USD',
    currencySymbol: '$'
  } : {
    currencyName: '',
    currencyCode: currencyCode || '',
    currencySymbol: ''
  };
};

/**
 * Helper function to find the appropriate offering based on billing period
 * @param offerings Array of PlanOffering
 * @param billingPeriod Optional billing period string
 * @returns The matched PlanOffering or the first offering
 */
var findAppropriateOffering = function findAppropriateOffering(offerings, billingPeriod) {
  var _offerings$find;
  return billingPeriod ? (_offerings$find = offerings.find(function (offering) {
    return offering.plan.billingPeriod === billingPeriod;
  })) !== null && _offerings$find !== void 0 ? _offerings$find : offerings[0] : offerings[0];
};

/**
 * Converts a product-recommendation-dgs plan offering to a digital-pricing-bff
 * GetPricingV2Data
 * @param productOffering PlanOffering; product-recommendation-dgs query response
 * @param experiment string; Optimizely experiment
 * @param countryCode string; ISO 3166 A2 code
 * @returns GetPricingV2Data
 */
var toGetPricingV2Data = function toGetPricingV2Data(_ref, inputs) {
  var _plan$price, _plan$price2, _plan$price3, _plan$price$currency, _plan$price4, _plan$price5;
  var _ref$productRecommend = _ref.productRecommendation,
    productRecommendation = _ref$productRecommend === void 0 ? {
      offerings: []
    } : _ref$productRecommend;
  var countryCode = inputs.countryCode,
    productCode = inputs.productCode,
    billingPeriod = inputs.billingPeriod;
  var offerings = productRecommendation.offerings;
  if (offerings.length === 0) {
    return getEmptyOfferingResponse(inputs);
  }
  var productOffering = findAppropriateOffering(offerings, billingPeriod);
  var plan = productOffering.plan,
    allowedActions = productOffering.allowedActions;
  var productFamily = productCode;
  var currencyDetails = getCurrencyDetails((_plan$price = plan.price) === null || _plan$price === void 0 ? void 0 : _plan$price.currency);
  var itemDetails = getItemDetails(productOffering);
  var amount = (_plan$price2 = plan.price) === null || _plan$price2 === void 0 ? void 0 : _plan$price2.price;
  var priceWithCurrency = formatCurrency(amount, (_plan$price3 = plan.price) === null || _plan$price3 === void 0 ? void 0 : _plan$price3.currency);
  var price = {
    experiment: plan.experiment,
    price: amount.toString(),
    pricingLogId: (_plan$price$currency = (_plan$price4 = plan.price) === null || _plan$price4 === void 0 ? void 0 : _plan$price4.currency) !== null && _plan$price$currency !== void 0 ? _plan$price$currency : '',
    priceSheetUsageBillingPeriodUnit: '',
    priceSheetUsageBillingPeriodNumber: '',
    packagePoid: null,
    itemDetails: itemDetails,
    billingPeriod: plan.billingPeriod,
    currency: (_plan$price5 = plan.price) === null || _plan$price5 === void 0 ? void 0 : _plan$price5.currency,
    priceWithCurrency: priceWithCurrency
  };
  var result = {
    eligibility: {
      actions: allowedActions,
      finalActions: allowedActions,
      currencyDetails: currencyDetails,
      eligible: true,
      order: null,
      price: price,
      externalId: plan.code,
      pricePlans: getPricePlans(offerings, billingPeriod)
    },
    externalId: plan.code,
    offerId: [],
    countryCode: countryCode,
    productFamily: productFamily,
    nextInteraction: null,
    currencyDetails: currencyDetails,
    price: price,
    actions: allowedActions[0],
    vendorType: VendorType.RECURLY,
    authState: getAuthState(),
    networkInfo: {
      isAnonymous: false
    }
  };
  return result;
};
var getPricePlans = function getPricePlans(offerings, billingPeriod) {
  var baseOffering = findAppropriateOffering(offerings, billingPeriod);
  var price = baseOffering.plan.price;
  var priceQuoteItems = offerings.filter(function (offering) {
    return billingPeriod ? offering.plan.billingPeriod === billingPeriod : true;
  }).map(function (offering) {
    var _price$currency;
    var plan = offering.plan;
    return _objectSpread({
      itemDetails: getItemDetails(offering),
      pricingKey: {
        productId: plan.id,
        experimentId: plan.experiment
      },
      billingPeriod: plan.billingPeriod,
      pricingLogId: (_price$currency = price === null || price === void 0 ? void 0 : price.currency) !== null && _price$currency !== void 0 ? _price$currency : '',
      priceQuoteErrorCodes: []
    }, calculateSavings(offering));
  });
  return {
    itemTestName: '',
    itemTestVariation: '',
    quoteItems: {
      currencyCode: price === null || price === void 0 ? void 0 : price.currency,
      priceQuoteItems: priceQuoteItems
    }
  };
};
var getItemDetails = function getItemDetails(plan) {
  var _plan$plan = plan.plan,
    id = _plan$plan.id,
    code = _plan$plan.code,
    trialPeriod = _plan$plan.trialPeriod,
    family = _plan$plan.family;
  var productFamily = PRODUCT_FAMILY_TYPE_MAP.get(family);
  return {
    id: id,
    externalId: code,
    trialDays: trialPeriod,
    productFamily: productFamily,
    recurringServices: [],
    taxProductClass: ''
  };
};
var calculateSavings = function calculateSavings(planOffer) {
  var _basePlan$price$price, _basePlan$price;
  var _planOffer$plan = planOffer.plan,
    price = _planOffer$plan.price,
    billingPeriod = _planOffer$plan.billingPeriod,
    basePlan = planOffer.basePlan;
  var months = 1;
  switch (billingPeriod) {
    case 'P6M':
      months = 6;
      break;
    case 'P12M':
    case 'P1Y':
      months = 12;
      break;
    default:
      months = 1;
  }
  var amount = price === null || price === void 0 ? void 0 : price.price;
  var currencyValue = price === null || price === void 0 ? void 0 : price.currency;
  var baseMonthlyPrice = (_basePlan$price$price = basePlan === null || basePlan === void 0 || (_basePlan$price = basePlan.price) === null || _basePlan$price === void 0 ? void 0 : _basePlan$price.price) !== null && _basePlan$price$price !== void 0 ? _basePlan$price$price : amount;
  var expectedBasePrice = months * baseMonthlyPrice;
  var totalItemPrice = amount;
  var totalItemPriceFormatted = formatCurrency(totalItemPrice, currencyValue);
  var monthlyPrice = totalItemPrice / months;
  var percentageSavings = ((baseMonthlyPrice - monthlyPrice) / baseMonthlyPrice * 100).toFixed(0);
  var result = {
    expectedBasePrice: formatCurrency(expectedBasePrice, currencyValue),
    monthlySavings: formatCurrency(expectedBasePrice - amount, currencyValue),
    totalBeforeTax: totalItemPriceFormatted,
    totalItemPrice: totalItemPriceFormatted,
    unitPrice: totalItemPriceFormatted,
    totalSavings: formatCurrency(expectedBasePrice - totalItemPrice, currencyValue),
    totalSavingsPercentage: percentageSavings.toString(),
    monthlyPrice: formatCurrency(parseFloat(monthlyPrice.toFixed(2)), currencyValue),
    monthlyBasePrice: formatCurrency(baseMonthlyPrice, currencyValue),
    optionPreference: {
      defaultItem: months == 1,
      bestValue: months == 12
    },
    estimatedTax: '0',
    discount: '0'
  };
  return result;
};
var getEmptyOfferingResponse = function getEmptyOfferingResponse(_ref2) {
  var _ref2$experiment = _ref2.experiment,
    experiment = _ref2$experiment === void 0 ? '' : _ref2$experiment,
    countryCode = _ref2.countryCode,
    productCode = _ref2.productCode;
  var price = {
    experiment: experiment,
    price: '',
    pricingLogId: '',
    priceSheetUsageBillingPeriodUnit: '',
    priceSheetUsageBillingPeriodNumber: '',
    packagePoid: null,
    itemDetails: {
      id: '',
      externalId: '',
      trialDays: '',
      productFamily: productCode,
      recurringServices: [],
      taxProductClass: ''
    },
    billingPeriod: '',
    currency: '',
    priceWithCurrency: ''
  };
  return {
    eligibility: {
      actions: [],
      finalActions: [],
      currencyDetails: getCurrencyDetails(),
      eligible: true,
      order: null,
      price: price,
      externalId: '',
      pricePlans: {
        itemTestName: '',
        itemTestVariation: '',
        quoteItems: {
          currencyCode: '',
          priceQuoteItems: []
        }
      }
    },
    externalId: '',
    offerId: [],
    countryCode: countryCode,
    productFamily: productCode,
    nextInteraction: null,
    currencyDetails: getCurrencyDetails(),
    price: price,
    actions: '',
    vendorType: VendorType.RECURLY,
    authState: isWindowDefined ? getAuthState() : {},
    networkInfo: {
      isAnonymous: false
    }
  };
};
export { toGetPricingV2Data, toPricingSdkProductType, getCurrencyDetails, toDGSProductType, findAppropriateOffering, getPricePlans };


//////////////////
// WEBPACK FOOTER
// ./node_modules/@chegg/writing-checkout/node_modules/@chegg/commerce-utils/dist/esm/utils/PRSUtils/product-recommendation-dgs.util.js
// module id = 1402
// module chunks = 11